import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import "./ResetPasswordForm.scss";
import CircularProgress from "@mui/material/CircularProgress";
import logo from "./logoTaDark.png";
import TextField from "@mui/material/TextField";
import { useHistory, useLocation } from "react-router-dom";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { auth } from "../../firebase/firebase";

const ResetPasswordForm = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLengthError, setShowLengthError] = useState(false);
  const [showMatchError, setShowMatchError] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(true);
  const [firebaseError, setFirebaseError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [oobCode, setOobCode] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Parse URL parameters
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const continueUrl = params.get("continueUrl");

    // Extract referrer from continueUrl if present
    let referrer = params.get("referrer");
    if (continueUrl && !referrer) {
      try {
        const decodedContinueUrl = decodeURIComponent(continueUrl);
        const continueUrlParams = new URLSearchParams(
          decodedContinueUrl.split("?")[1],
        );
        referrer = continueUrlParams.get("referrer");
      } catch (error) {
        console.error("Error parsing continueUrl:", error);
      }
    }

    const result = {
      mode: params.get("mode"),
      oobCode: params.get("oobCode"),
      continueUrl: continueUrl,
      referrer: referrer,
    };

    console.log("Parsed URL params:", result); // Debug log
    return result;
  }, [location.search]);

  // Verify the password reset code when component mounts
  useEffect(() => {
    const verifyResetCode = async () => {
      console.log("URL Params:", urlParams); // Debug log
      console.log("Current URL:", window.location.href); // Debug log

      if (!urlParams.oobCode || urlParams.mode !== "resetPassword") {
        console.log("Missing oobCode or wrong mode"); // Debug log
        setIsLinkExpired(true);
        setVerifyingCode(false);
        return;
      }

      try {
        console.log("Verifying code:", urlParams.oobCode); // Debug log
        // Verify the password reset code and get the user's email
        const email = await auth.verifyPasswordResetCode(urlParams.oobCode);
        console.log("Verification successful, email:", email); // Debug log
        setUserEmail(email);
        setOobCode(urlParams.oobCode);
        setVerifyingCode(false);
      } catch (error) {
        console.error("Error verifying password reset code:", error);
        console.log("Error code:", error.code); // Debug log
        console.log("Error message:", error.message); // Debug log
        setIsLinkExpired(true);
        setVerifyingCode(false);

        // Handle specific error codes
        if (error.code === "auth/expired-action-code") {
          setFirebaseError("Password reset link has expired");
        } else if (error.code === "auth/invalid-action-code") {
          setFirebaseError("Invalid or already used password reset link");
        } else {
          setFirebaseError("Invalid password reset link");
        }
      }
    };

    verifyResetCode();
  }, [urlParams]);

  const handleSubmit = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      setShowMatchError(true);
      return;
    }
    if (newPassword.length < 8) {
      setShowLengthError(true);
      return;
    }

    setLoading(true);
    setFirebaseError("");

    try {
      // Confirm the password reset with the new password
      await auth.confirmPasswordReset(oobCode, newPassword);
      setSubmitted(true);

      // Optional: Auto-redirect after successful password reset
      // setTimeout(() => {
      //   goBackToLogin();
      // }, 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
      setLoading(false);

      // Handle specific error codes
      if (error.code === "auth/expired-action-code") {
        setIsLinkExpired(true);
        setFirebaseError("Password reset link has expired");
      } else if (error.code === "auth/invalid-action-code") {
        setIsLinkExpired(true);
        setFirebaseError("Invalid or already used password reset link");
      } else if (error.code === "auth/weak-password") {
        setFirebaseError(
          "Password is too weak. Please choose a stronger password.",
        );
      } else {
        setFirebaseError("Failed to reset password. Please try again.");
      }
    }
  }, [newPassword, confirmPassword, oobCode]);

  const requestNewLink = () => {
    if (urlParams.continueUrl) {
      try {
        const url = new URL(decodeURIComponent(urlParams.continueUrl));
        window.location.href = `${url.protocol}//${url.host}/reset-password`;
      } catch (_error) {
        history.push("/reset-password");
      }
    } else {
      history.push("/reset-password");
    }
  };

  const goBackToLogin = () => {
    if (urlParams.continueUrl) {
      try {
        const url = new URL(decodeURIComponent(urlParams.continueUrl));
        window.location.href = `${url.protocol}//${url.host}/signin`;
      } catch (_error) {
        history.push("/signin");
      }
    } else {
      history.push("/signin");
    }
  };

  useEffect(() => {
    // Reset errors when password changes
    setShowLengthError(false);
    setShowMatchError(false);
    setFirebaseError("");
  }, [newPassword, confirmPassword]);

  // Show loading while verifying the code
  if (verifyingCode) {
    return (
      <div className="reset_password_form">
        <div className="right-part">
          <div className="reset-password-form-header">
            <CircularProgress size="40px" />
            <div className="reset-password-form-header-content">
              <div className="reset-password-form-header-content-title">
                Verifying reset link...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="reset_password_form">
        <div className="right-part">
          {isLinkExpired ? (
            <>
              <div className="reset-password-form-header reset-password-form-header">
                <WarningRoundedIcon className="reset-password-form-header-icon" />
                <div className="reset-password-form-header-content">
                  <div className="reset-password-form-header-content-title">
                    Password link expired
                  </div>
                  <div className="reset-password-form-header-content-detail">
                    {firebaseError ||
                      "Password link has expired or has been used"}
                  </div>
                </div>
              </div>

              <Button
                className="reset-password-form-button reset-password-back-button"
                variant="contained"
                style={{ width: "340px" }}
                onClick={requestNewLink}
              >
                Request a new link
              </Button>
            </>
          ) : submitted ? (
            <>
              <div className="reset-password-form-header reset-password-form-header">
                <img
                  className="reset-password-logo cursor-pointer"
                  alt=""
                  height={36}
                  src={logo}
                  onClick={goBackToLogin}
                />
                <div className="reset-password-form-header-content">
                  <div className="reset-password-form-header-content-title">
                    Password Changed!
                  </div>
                  <div className="reset-password-form-header-content-detail">
                    You can now login with your new password
                  </div>
                </div>
              </div>

              <Button
                className="reset-password-form-button reset-password-back-button"
                variant="contained"
                onClick={goBackToLogin}
                style={{ width: "340px" }}
              >
                Back to Login
              </Button>
            </>
          ) : (
            <>
              <div className="reset-password-form-header reset-password-form-header">
                <img
                  className="reset-password-logo cursor-pointer"
                  alt=""
                  height={36}
                  src={logo}
                  onClick={goBackToLogin}
                />
                <div className="reset-password-form-header-content">
                  <div className="reset-password-form-header-content-title">
                    Reset your password
                  </div>
                  <div className="reset-password-form-header-content-detail">
                    {userEmail
                      ? `Enter a new password for ${userEmail}`
                      : "Enter a new password to change your password"}
                  </div>
                </div>
              </div>

              <div className="reset-password-form-footer reset-password-form-footer">
                <TextField
                  error={showMatchError}
                  className="reset-password-text"
                  label="New password"
                  variant="standard"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff
                              style={{ color: "rgba(0, 0, 0, 0.56)" }}
                            />
                          ) : (
                            <Visibility
                              style={{ color: "rgba(0, 0, 0, 0.56)" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  type={showPassword ? "text" : "password"}
                />

                <TextField
                  error={showMatchError}
                  className="reset-password-text"
                  label="Re-enter new password"
                  variant="standard"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff
                              style={{ color: "rgba(0, 0, 0, 0.56)" }}
                            />
                          ) : (
                            <Visibility
                              style={{ color: "rgba(0, 0, 0, 0.56)" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  type={showConfirmPassword ? "text" : "password"}
                />

                {showMatchError && (
                  <div className="password-match-error">
                    <WarningRoundedIcon />
                    Passwords don&apos;t match
                  </div>
                )}

                {showLengthError && (
                  <div className="password-match-error">
                    <WarningRoundedIcon />
                    Password should be at least 8 characters
                  </div>
                )}

                {firebaseError && (
                  <div className="password-match-error">
                    <WarningRoundedIcon />
                    {firebaseError}
                  </div>
                )}

                <Button
                  className="reset-password-form-button"
                  variant="contained"
                  disabled={showMatchError || loading}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <CircularProgress size="25px" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPasswordForm;
